import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { styled } from "simplydo/react/styled";
import { Divider } from "simplydo/react/ui/divider";
import { H3 } from "simplydo/react/ui/header";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";

import { useAppSelector } from "store";
import util from "utils/utils";

import { EmptyBox, MultiLineParagraph } from "components/lib/UI";
import AchievementComponent from "components/lib/Users/Achievement";

import type { PublicUser } from "../types";

const ProfileDiv = styled.div`
  .overview {
    font-size: 1.2rem;
  }
  .achievements {
    margin-top: 10px;
    .achievements-title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      h3,
      h5 {
        display: block;
        margin-bottom: 0;
        margin-top: 0;
      }
    }
    .achievements-list {
      display: flex;
      gap: 16px;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }
  }
`;

type ProfileDetailsProps = {
  profileUser: PublicUser;
  isYourProfile: boolean;
  loading: boolean;
};

export const ProfileDetails = ({
  profileUser,
  isYourProfile,
  loading,
}: ProfileDetailsProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const { overview, video, videoUrl } = profileUser?.profile ?? {};

  return (
    <ProfileDiv>
      {loading ? (
        <Loader />
      ) : (
        <>
          {(overview || video) && (
            <H3 style={{ textTransform: "uppercase" }}>
              {t("generic.overview")}
            </H3>
          )}
          {videoUrl ? (
            <VideoPlayer style={{ margin: "30px auto" }} src={videoUrl} />
          ) : null}
          {overview ? (
            <div className="overview">
              <MultiLineParagraph content={overview} />
            </div>
          ) : null}
          {(overview || video) && <Divider hidden />}
          {profileUser?.ownerTags?.length ? (
            <div style={{ marginTop: 20 }}>
              <H3 style={{ textTransform: "uppercase" }}>
                {t("generic.tags")}
              </H3>
              {profileUser?.ownerTags.map((tag) => (
                <Label
                  style={{ marginTop: 3 }}
                  key={tag._id}
                  size="large"
                  circular
                >
                  {tag.value}
                </Label>
              ))}
              <Divider hidden />
            </div>
          ) : null}

          {(isYourProfile ||
            util.hasPermission(
              user,
              "org.viewDashboard",
              user?.ownerOrganisation?._id,
            )) && (
            <div className="achievements">
              <div className="achievements-title">
                <div>
                  <H3 style={{ textTransform: "uppercase" }}>
                    {t("generic.achievements")}
                  </H3>
                </div>
                <div>
                  <Link className="with-border" to={"/achievements"}>
                    View all achievements
                  </Link>
                </div>
              </div>
              {!profileUser?.achievements?.length &&
              !profileUser?.customAchievements?.length ? (
                <EmptyBox title="No achievements unlocked" />
              ) : (
                <div className="achievements-list">
                  {profileUser?.customAchievements?.map((a) => (
                    <AchievementComponent
                      showAllInfo
                      key={a._id}
                      achievement={a}
                    />
                  ))}
                  {profileUser?.achievements?.map((achievement) => (
                    <AchievementComponent
                      showAllInfo
                      key={achievement?.key}
                      achievement={achievement}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </ProfileDiv>
  );
};
